import React, {
  useCallback, useEffect, useMemo
} from 'react';
import { useHistory, useParams } from 'react-router-dom';

import capitalize from 'lodash/capitalize';

import { usePipelineExternalUrl, usePipelineInternalUrl } from '../../../utils/hooks/use_pipeline_access_points';
import usePageTitle from '../../../utils/hooks/use_page_title';
import { useRedirectOnWorkspaceChange } from '../../../utils/hooks/use_redirect_on_value_change';

import PipelineIcon from '../../common/icons/PipelineIcon';
import VersionIcon from '../../common/icons/VersionIcon';

import ButtonTabGroup from '../../common/ButtonTabGroup/ButtonTabGroup';
import ClipboardLabel from '../../common/ClipboardLabel/ClipboardLabel';
import HeadingLabel from '../../common/Labels/HeadingLabel';
import Icon from '../../common/icons/Icon';
import InfoTooltip from '../../common/InfoTooltip/InfoTooltip';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import SubHeadingLabel from '../../common/Labels/SubHeadingLabel';

import PageLayout from '../../pages/PageLayout';
import PrimaryContent from '../../pages/layouts/PrimaryContent';
import SecondaryContent from '../../pages/layouts/SecondaryContent';

import ContentTags from '../../ContentTags/ContentTags';
import PageNotFound from '../../pages/PageNotFound';
import PipelineActivityLog from '../PipelineActivityLog/PipelineActivityLog';
import PipelineAnalyticsTab from './tabs/PipelineAnalyticsTab';
import PipelineDeploymentPanel from '../PipelineDeploymentPanel/PipelineDeploymentPanel';
import PipelineDetailTab from './tabs/PipelineDetailTab';
import PipelineInsightsTab from './tabs/PipelineInsightsTab';
import PipelineMetricsTab from './tabs/PipelineMetricsTab';
import PipelineStatusBadge from '../PipelineStatusBadge/PipelineStatusBadge';

import { PipelineDataProvider, usePipelineData } from '../PipelineDataContext';
import { AssayLoadingProvider } from '../../assays/AssayLoadingContext';
import { PipelineCommentsProvider } from '../../contexts/CommentData/CommentData';
import { TimeRangeProvider } from '../../contexts/TimeRangeContext/TimeRangeContext';
import useClientContext from '../../contexts/ClientData/ClientData';

import EdgeLocationFilter from '../../edge/EdgeLocationFilter/EdgeLocationFilter';
import { PipelinePublishPackageDetailsProvider } from './tabs/PipelinePublishPackageDetails';

import { PublishPipelineProvider, usePipelineEdgeData } from '../../VersionHistoryTimeline/PublishPipelineContext';

const PipelineNotLoadedSection = ({ error }) => error ? (
  <SubHeadingLabel className="ml-2 mt-2" size="2xl">
    Something went wrong loading this Pipeline
  </SubHeadingLabel>
) : (
  <div className="ml-2 mt-2 flex flex-row">
    <SubHeadingLabel size="2xl">
      Loading Pipeline Data
    </SubHeadingLabel>
    <LoadingSpinner className="ml-2" animationKey="pipeline-detail-loading-spinner" />
  </div>
)

const InferenceURLSection = ({
  externalUrl,
  internalUrl,
  areExternalInferenceURLsEnabled = false,
}) => (
  <div className="ml-2 mt-2 flex flex-col items-start">
    <div className="flex">
      <InfoTooltip
        label="Available outside of the Kubernetes environment such as public internet"
      />
      <SubHeadingLabel className="ml-1" bold>
        External URL:
      </SubHeadingLabel>
      {
        areExternalInferenceURLsEnabled ? (
          <ClipboardLabel
            className="ml-1"
            iconSize={16}
            displayLabel={externalUrl}
            labelValue={externalUrl}
            LabelComponent={SubHeadingLabel}
            showClipboardIconOnHover
          />
        ) : (
          <SubHeadingLabel className="ml-1">
            External Inferencing is off
          </SubHeadingLabel>
        )
      }
    </div>
    <div className="flex">
      <InfoTooltip
        label="Available only through internal Kubernetes environment hosting the Wallaroo instance such as Wallaroo's JupyterHub"
        direction="se"
      />
      <SubHeadingLabel className="ml-1" bold>
        Internal URL:
      </SubHeadingLabel>
      <ClipboardLabel
        className="ml-1"
        iconSize={16}
        displayLabel={internalUrl}
        labelValue={internalUrl}
        LabelComponent={SubHeadingLabel}
        showClipboardIconOnHover
      />
    </div>
  </div>
)

export const PipelineDetailPage = ({
  pipeline,
  loading,
  error,
  isAnomalyDetectionEnabled,
  areExternalInferenceURLsEnabled,
  handleAddTag,
  handleDeleteTag,
}) => {

  const routeParams = useParams();
  const history = useHistory();

  const { areAssaysEnabled, isEdgeEnabled } = useClientContext();
  const { isEdgeSelected } = usePipelineEdgeData();

  const showInferenceEndpoints = !isEdgeEnabled || !isEdgeSelected;
  const disableDetailTab = isEdgeEnabled && isEdgeSelected;

  const PIPELINE_DETAIL_TABS = useMemo(() => ({
    Details: 'Details',
    Metrics: 'Metrics',
    ...(areAssaysEnabled ? { Insights: 'Insights' } : {}),
    Analytics: 'Analytics',
  }), [areAssaysEnabled]);

  const pageTabs = useMemo(() => {
    const options = Object.values(PIPELINE_DETAIL_TABS);
    return options.map((option) => {
      if (option === PIPELINE_DETAIL_TABS.Analytics) {
        return {
          value: option,
          disabled: !isAnomalyDetectionEnabled || (isEdgeEnabled && isEdgeSelected),
          id: `${option.toLowerCase()}-tab-btn`,
        }
      }
      if (option === PIPELINE_DETAIL_TABS.Details) {
        return {
          value: option,
          tooltipText: disableDetailTab ? 'Details specific to edges are not available' : null,
          disabled: disableDetailTab,
          id: `${option.toLowerCase()}-tab-btn`,
        }
      }
      return {
        value: option,
        id: `${option.toLowerCase()}-tab-btn`,
      }
    })
  }, [PIPELINE_DETAIL_TABS, disableDetailTab, isAnomalyDetectionEnabled, isEdgeEnabled, isEdgeSelected]);

  const workspaceId = routeParams?.workspaceId;
  const pipelineId = routeParams?.id;
  const pipelineVersion = routeParams?.version;
  const selectedTab = routeParams?.selectedTab;

  const isDetailSelected = selectedTab?.length && selectedTab.toLowerCase() === PIPELINE_DETAIL_TABS.Details.toLowerCase();
  const capitalizedTab = selectedTab?.length ? capitalize(selectedTab) : 'Details';

  const { push: setRoute, replace: updateRoute } = history;

  const { externalUrl } = usePipelineExternalUrl(workspaceId, pipelineId);
  const { internalUrl } = usePipelineInternalUrl(workspaceId, pipelineId);

  const onTabSelection = useCallback((newTab) => {
    setRoute(`/workspaces/${workspaceId}/pipelines/${pipelineId}/versions/${pipelineVersion}/${newTab.toLowerCase()}`)
  }, [pipelineId, pipelineVersion, setRoute, workspaceId]);

  useEffect(() => {
    if (!selectedTab?.length) {
      // Make sure details is part of the routing on first visit
      updateRoute(`/workspaces/${workspaceId}/pipelines/${pipelineId}/versions/${pipelineVersion}/details`)
    }
  }, [pipelineId, pipelineVersion, selectedTab, workspaceId, setRoute, updateRoute])

  useEffect(() => {
    if (disableDetailTab && isDetailSelected) {
      updateRoute(`/workspaces/${workspaceId}/pipelines/${pipelineId}/versions/${pipelineVersion}/metrics`)
    }
  }, [isDetailSelected, disableDetailTab, PIPELINE_DETAIL_TABS.Metrics, onTabSelection, updateRoute, workspaceId, pipelineId, pipelineVersion])

  usePageTitle(`${pipeline ? pipeline.pipelineID : 'Pipeline'} | Wallaroo`);

  if (!loading && !error && !pipeline) {
    return <PageNotFound pipelineID={routeParams.id} pipelineVersion={routeParams.version} />;
  }

  const isDeployed = pipeline?.deployment?.deployed ?? false;

  return (
    <div className="flex flex-col w-full mb-16">
      <div className="bg-brand border-t-2 border-border-brand text-white flex justify-center items-center">
        <div className="px-16 py-8 w-full flex-grow flex flex-col items-start">
          <div className="flex items-center">
            <Icon icon={PipelineIcon} size={80} useTailwindSizing />
            <HeadingLabel className="ml-2 mt-1" size="4xl">
              {routeParams?.id}
            </HeadingLabel>
          </div>
          <div className="ml-12 mb-2 flex items-center">
            <Icon icon={VersionIcon} size={32} useTailwindSizing />
            <SubHeadingLabel className="mx-2" size="2xl">
              {routeParams?.version}
            </SubHeadingLabel>
            {
              isEdgeEnabled && (
                <EdgeLocationFilter />
              )
            }
          </div>
          {
            !pipeline ? (
              <PipelineNotLoadedSection error={error} />
            ) : (
              <>
                <div className="ml-2 mt-2 flex items-center">
                  <PipelineStatusBadge status={isDeployed ? 'Active' : 'Inactive'} />
                  <div className="ml-2 w-4/5 text-brand">
                    <ContentTags
                      tags={pipeline.pipelineTags.map(({ tag }) => tag.tag)}
                      onAddTag={handleAddTag}
                      onDeleteTag={handleDeleteTag}
                    />
                  </div>
                </div>
                {
                  showInferenceEndpoints && (
                    <InferenceURLSection
                      areExternalInferenceURLsEnabled={areExternalInferenceURLsEnabled}
                      externalUrl={externalUrl}
                      internalUrl={internalUrl}
                    />
                  )
                }
              </>
            )
          }
        </div>
      </div>
      {!error && (
        <TimeRangeProvider>
          <div className="my-4 px-16 ml-2">
            <ButtonTabGroup
              selectedOption={capitalizedTab}
              options={pageTabs}
              onItemSelected={onTabSelection}
            />
          </div>
          <PageLayout>
            <PrimaryContent>
              {
                capitalizedTab === PIPELINE_DETAIL_TABS.Details && (
                  <PipelinePublishPackageDetailsProvider>
                    <PipelineDetailTab workspaceId={workspaceId} />
                  </PipelinePublishPackageDetailsProvider>
                )
              }
              {
                capitalizedTab === PIPELINE_DETAIL_TABS.Metrics && (
                  <PipelineMetricsTab externalUrl={externalUrl} />
                )
              }
              {
                capitalizedTab === PIPELINE_DETAIL_TABS.Analytics && (
                  <PipelineAnalyticsTab />
                )
              }
              {
                capitalizedTab === PIPELINE_DETAIL_TABS.Insights && (
                  <PipelineInsightsTab />
                )
              }
            </PrimaryContent>
            <SecondaryContent>
              <PipelineDeploymentPanel />
              <PipelineCommentsProvider pipeline={pipeline}>
                <PipelineActivityLog pipeline={pipeline} />
              </PipelineCommentsProvider>
            </SecondaryContent>
          </PageLayout>
        </TimeRangeProvider>
      )}
    </div>
  );
};

const ContextualPipelineDetailPage = (props) => {
  const {
    pipeline,
    loading,
    error,
    isAnomalyDetectionEnabled,
    addPipelineTag,
    deletePipelineTag,
  } = usePipelineData();
  const { areExternalInferenceURLsEnabled } = useClientContext();

  useRedirectOnWorkspaceChange('/pipelines')

  return (
    <PipelineDetailPage
      pipeline={pipeline}
      loading={loading}
      error={error}
      isAnomalyDetectionEnabled={isAnomalyDetectionEnabled}
      areExternalInferenceURLsEnabled={areExternalInferenceURLsEnabled}
      handleAddTag={addPipelineTag}
      handleDeleteTag={deletePipelineTag}
      {...props}
    />
  )
}

const WrappedPipelineDetailPage = props => (
  <PipelineDataProvider>
    <PublishPipelineProvider>
      <AssayLoadingProvider>
        <ContextualPipelineDetailPage {...props} />
      </AssayLoadingProvider>
    </PublishPipelineProvider>
  </PipelineDataProvider>
)

export default WrappedPipelineDetailPage;
